// Story 023 — manual-draw overlay.
//
// ANA-R3-GEOM-03 — hosts the shared PolygonEditor (GEOM-02) for the free-form
// polygon/polyline modes, and keeps the Story-023 marquee drag for rectangle
// mode (the fast default path — "Drag to draw"). The overlay covers the Viewer
// slot when drawStore.enabled === true.
//
//   - rect      → left-drag a marquee; on release the 4 corners (as a closed
//                 quad, first corner repeated) become the draft.
//   - polygon   → PolygonEditor: click vertices, Enter/first-vertex closes.
//   - polyline  → PolygonEditor: click vertices, Enter/double-click finishes.
//
// Geometry is driven through an OSD-backed CoordinateTransform built from the
// ViewerViewportApi the Viewer publishes (so the in-progress shape re-projects
// on pan/zoom — AC3). On finish the normalised vertices become a flat PIXEL
// path (multiplied by image dims) and are pushed to drawStore.draft, which the
// DefectDrawer reads.
//
// Mousewheel zoom + pan must stay OSD's (AC3). The overlay sits above the OSD
// canvas, so wheel/right-drag/middle-drag events would otherwise be swallowed.
// We forward them to the Viewer's wrapper, whose own (non-passive) listeners
// drive OSD — keeping every zoom/pan gesture alive mid-draw.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box, Snackbar } from "@mui/material";
import {
  PolygonEditor,
  serialisePoints,
  type CoordinateTransform,
  type Point,
  type PolygonEditorApi,
  type PolygonValue,
} from "@aeriosiq/ui";
import { useDrawStore, bboxOfPath, type DrawMode } from "../state/drawStore.js";
import { useViewportApiStore } from "../state/viewportApiStore.js";
import { useEscapeLayer } from "../state/useEscapeLayer.js";
import { ESCAPE_PRIORITY } from "../state/escapeStack.js";

interface DrawOverlayProps {
  imageId: string;
  imageWidth: number;
  imageHeight: number;
}

const VIEWER_WRAPPER_SEL = '[data-testid="viewer-wrapper"]';
const VIEWER_CELL_SEL = "[data-active-image-id]";
/** Minimum marquee size in IMAGE px (Story 023 AC12) below which a rect drag is
 *  treated as a stray click and silently dismissed. */
const MIN_RECT_PX = 10;
/** ANA-R4-DRAW-01 — a right-click that moves less than this (screen px) counts as
 *  a stationary click (close polygon / finish line) rather than a zoom-rect drag. */
const RIGHT_CLICK_MOVE_PX = 6;

interface ScreenPt {
  x: number;
  y: number;
}

export function DrawOverlay({
  imageId,
  imageWidth,
  imageHeight,
}: DrawOverlayProps): React.ReactElement | null {
  const enabled = useDrawStore((s) => s.enabled);
  const mode = useDrawStore((s) => s.mode);
  const setMode = useDrawStore((s) => s.setMode);
  const setDraft = useDrawStore((s) => s.setDraft);
  const setEnabled = useDrawStore((s) => s.setEnabled);
  const draft = useDrawStore((s) => s.draft);
  const viewportApi = useViewportApiStore((s) => s.api);

  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const apiRef = useRef<PolygonEditorApi | null>(null);
  // ANA-R4-DRAW-01 — screen position of the last right-button-down, so a
  // stationary right-click (no drag) can close the polygon / finish the line.
  const rightDownRef = useRef<ScreenPt | null>(null);
  // ANA-R4-VALID-01 — show a "crossing lines — prohibited" notice when a finish
  // is refused for self-intersection.
  const [invalidOpen, setInvalidOpen] = useState(false);
  const [size, setSize] = useState<{ width: number; height: number }>({
    width: 0,
    height: 0,
  });

  // The committed in-progress polygon/polyline shape (no closing duplicate).
  const [points, setPoints] = useState<Point[]>([]);
  const pointsRef = useRef<Point[]>(points);
  pointsRef.current = points;

  // Rect-mode marquee (screen-space, relative to the overlay).
  const [marquee, setMarquee] = useState<{
    start: ScreenPt;
    end: ScreenPt;
  } | null>(null);

  // Track the wrapper size so the editor's Konva Stage matches the overlay.
  useEffect(() => {
    const node = wrapperRef.current;
    if (!node) return;
    const measure = () => {
      const r = node.getBoundingClientRect();
      setSize({ width: r.width, height: r.height });
    };
    measure();
    const ro =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(measure)
        : null;
    ro?.observe(node);
    return () => {
      ro?.disconnect();
    };
  }, [enabled]);

  // Build a CoordinateTransform over the published OSD viewport API. When the
  // Viewer hasn't published yet (pre-open, or jsdom), fall back to a linear
  // surface transform so the editor still works in tests without OSD.
  const transform = useMemo<CoordinateTransform>(() => {
    if (viewportApi) {
      return {
        imageToScreen: (p: Point) => viewportApi.imageToScreen(p),
        screenToImage: (p) => viewportApi.screenToImage(p),
        version: viewportApi.version,
      };
    }
    const w = size.width || 1;
    const h = size.height || 1;
    return {
      imageToScreen: (p: Point) => ({ x: p.x * w, y: p.y * h }),
      screenToImage: (p) => ({ x: p.x / w, y: p.y / h }),
      version: w * 100000 + h,
    };
  }, [viewportApi, size.width, size.height]);

  const value: PolygonValue = useMemo(() => ({ mode, points }), [mode, points]);

  // Convert a committed normalised shape into a pixel draft.
  const commitShape = useCallback(
    (committed: PolygonValue) => {
      if (committed.points.length === 0) return;
      const normFlat = serialisePoints(committed);
      const pathPx: number[] = normFlat.map((v, i) =>
        i % 2 === 0 ? v * imageWidth : v * imageHeight,
      );
      const bb = bboxOfPath(pathPx);
      setDraft({ imageId, mode: committed.mode, pathPx, ...bb });
      setPoints([]);
    },
    [imageId, imageWidth, imageHeight, setDraft],
  );

  const handleChange = useCallback((next: PolygonValue) => {
    setPoints(next.points);
  }, []);

  const handleCommit = useCallback(
    (next: PolygonValue) => {
      commitShape(next);
    },
    [commitShape],
  );

  // Resolve the sibling Viewer wrapper (for forwarding gestures to OSD).
  const findViewerWrapper = useCallback((): HTMLElement | null => {
    const overlay = wrapperRef.current;
    if (!overlay) return null;
    return (
      overlay
        .closest(VIEWER_CELL_SEL)
        ?.querySelector<HTMLElement>(VIEWER_WRAPPER_SEL) ?? null
    );
  }, []);

  // Screen px (clientX/Y) → overlay-relative CSS px.
  const toOverlayPt = useCallback(
    (clientX: number, clientY: number): ScreenPt | null => {
      const node = wrapperRef.current;
      if (!node) return null;
      const r = node.getBoundingClientRect();
      return { x: clientX - r.left, y: clientY - r.top };
    },
    [],
  );

  // --- keyboard: mode hotkeys + D toggle. ESC is owned by the escape stack. ---
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target;
      if (target instanceof HTMLElement) {
        const tag = target.tagName.toLowerCase();
        if (
          tag === "input" ||
          tag === "textarea" ||
          tag === "select" ||
          target.isContentEditable
        ) {
          return;
        }
      }
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const k = e.key.toLowerCase();
      if (k === "d") {
        e.preventDefault();
        setEnabled(!enabled);
        return;
      }
      const hotkeyMode: DrawMode | null =
        k === "r"
          ? "rect"
          : k === "p"
            ? "polygon"
            : k === "l"
              ? "polyline"
              : null;
      if (hotkeyMode) {
        e.preventDefault();
        setPoints([]);
        setMarquee(null);
        setMode(hotkeyMode);
        if (!enabled) setEnabled(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
    };
  }, [enabled, setEnabled, setMode]);

  // ANA-R3-ESC-01 — draw layer. ESC clears an in-progress shape; if there's
  // nothing in progress it exits draw mode, then falls through to deselect.
  useEscapeLayer({
    id: "analysis:draw",
    priority: ESCAPE_PRIORITY.draw,
    isActive: enabled,
    onEscape: () => {
      apiRef.current?.cancel();
      if (pointsRef.current.length > 0 || marquee) {
        setPoints([]);
        setMarquee(null);
        return;
      }
      setEnabled(false);
    },
  });

  // Forward wheel to the Viewer's wrapper so OSD's listener drives zoom (AC3).
  const forwardWheel = useCallback(
    (e: WheelEvent): void => {
      const viewerWrapper = findViewerWrapper();
      if (!viewerWrapper) return;
      e.preventDefault();
      viewerWrapper.dispatchEvent(
        new WheelEvent(e.type, {
          deltaX: e.deltaX,
          deltaY: e.deltaY,
          deltaMode: e.deltaMode,
          clientX: e.clientX,
          clientY: e.clientY,
          ctrlKey: e.ctrlKey,
          metaKey: e.metaKey,
          bubbles: true,
          cancelable: true,
        }),
      );
    },
    [findViewerWrapper],
  );

  useEffect(() => {
    const node = wrapperRef.current;
    if (!node || !enabled) return;
    node.addEventListener("wheel", forwardWheel, { passive: false });
    return () => {
      node.removeEventListener("wheel", forwardWheel);
    };
  }, [enabled, forwardWheel]);

  // --- rect-mode marquee (Story 023 fast path) -------------------------------
  const rectActive = enabled && draft == null && mode === "rect";
  const marqueeDragRef = useRef(false);

  const handleMouseDownCapture = useCallback(
    (e: React.MouseEvent<HTMLDivElement>): void => {
      if (e.button === 0 && rectActive) {
        // Start a marquee.
        const p = toOverlayPt(e.clientX, e.clientY);
        if (!p) return;
        marqueeDragRef.current = true;
        setMarquee({ start: p, end: p });
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      if (e.button === 0) return; // left = polygon/polyline draw via editor
      // ANA-R4-DRAW-01 — remember where a right-button press started so the
      // contextmenu handler can tell a stationary click from a zoom drag.
      if (e.button === 2) rightDownRef.current = { x: e.clientX, y: e.clientY };
      // Secondary / middle → forward to OSD for marquee-zoom / pan.
      const viewerWrapper = findViewerWrapper();
      if (!viewerWrapper) return;
      viewerWrapper.dispatchEvent(
        new MouseEvent("mousedown", {
          button: e.button,
          buttons: e.buttons,
          clientX: e.clientX,
          clientY: e.clientY,
          bubbles: true,
          cancelable: true,
        }),
      );
    },
    [rectActive, toOverlayPt, findViewerWrapper],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>): void => {
      if (!marqueeDragRef.current) return;
      const p = toOverlayPt(e.clientX, e.clientY);
      if (!p) return;
      setMarquee((m) => (m ? { ...m, end: p } : null));
    },
    [toOverlayPt],
  );

  const finishMarquee = useCallback(
    (p: ScreenPt | null) => {
      if (!marqueeDragRef.current) return;
      marqueeDragRef.current = false;
      const m = marquee;
      setMarquee(null);
      if (!m) return;
      const end = p ?? m.end;
      // Screen → normalised image via the transform, then → pixel.
      const a = transform.screenToImage(m.start);
      const b = transform.screenToImage(end);
      const x0 = Math.min(a.x, b.x) * imageWidth;
      const y0 = Math.min(a.y, b.y) * imageHeight;
      const x1 = Math.max(a.x, b.x) * imageWidth;
      const y1 = Math.max(a.y, b.y) * imageHeight;
      if (x1 - x0 < MIN_RECT_PX || y1 - y0 < MIN_RECT_PX) return; // AC12
      // Closed quad (first corner repeated) — the rect-as-polygon wire shape.
      const pathPx = [x0, y0, x1, y0, x1, y1, x0, y1, x0, y0];
      const bb = bboxOfPath(pathPx);
      setDraft({ imageId, mode: "rect", pathPx, ...bb });
    },
    [marquee, transform, imageWidth, imageHeight, imageId, setDraft],
  );

  const handleMouseUp = useCallback(
    (e: React.MouseEvent<HTMLDivElement>): void => {
      if (marqueeDragRef.current) {
        finishMarquee(toOverlayPt(e.clientX, e.clientY));
      }
    },
    [finishMarquee, toOverlayPt],
  );

  const handleContextMenu = useCallback(
    (e: React.MouseEvent<HTMLDivElement>): void => {
      e.preventDefault();
      // ANA-R4-DRAW-01 — a single right-click WITHOUT movement closes the polygon
      // / finishes the line and opens the drawer. A right-DRAG stays the Viewer's
      // zoom-rect (handled by OSD), so we only act on a near-stationary click.
      const down = rightDownRef.current;
      rightDownRef.current = null;
      if (mode === "rect" || draft != null) return;
      if (down) {
        const moved = Math.hypot(e.clientX - down.x, e.clientY - down.y);
        if (moved > RIGHT_CLICK_MOVE_PX) return; // it was a zoom drag
      }
      apiRef.current?.closeOrFinishDraw();
    },
    [mode, draft],
  );

  // ANA-R4-VALID-01 — the editor refused a self-intersecting finish.
  const handleInvalidGeometry = useCallback(() => {
    setInvalidOpen(true);
  }, []);

  // The drawer is open while a draft exists — pause the editor so a stray click
  // behind the drawer doesn't start a new shape over the form.
  const editorActive = enabled && draft == null && mode !== "rect";

  if (!enabled) return null;

  // Marquee preview rectangle (overlay-relative CSS px).
  const marqueeRect = marquee
    ? {
        left: Math.min(marquee.start.x, marquee.end.x),
        top: Math.min(marquee.start.y, marquee.end.y),
        width: Math.abs(marquee.end.x - marquee.start.x),
        height: Math.abs(marquee.end.y - marquee.start.y),
      }
    : null;

  return (
    <Box
      ref={wrapperRef}
      data-testid="draw-overlay"
      data-draw-mode={mode}
      role="presentation"
      onMouseDownCapture={handleMouseDownCapture}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={(e) => {
        if (marqueeDragRef.current)
          finishMarquee(toOverlayPt(e.clientX, e.clientY));
      }}
      onContextMenu={handleContextMenu}
      sx={{
        position: "absolute",
        inset: 0,
        zIndex: 10,
        cursor: "crosshair",
      }}
    >
      {/* Polygon / polyline editor (not mounted in rect mode). */}
      {editorActive && size.width > 0 && size.height > 0 ? (
        <PolygonEditor
          value={value}
          onChange={handleChange}
          onCommit={handleCommit}
          onInvalidGeometry={handleInvalidGeometry}
          transform={transform}
          width={size.width}
          height={size.height}
          editable={editorActive}
          ariaLabel={`Draw defect (${mode})`}
          apiRef={(api) => {
            apiRef.current = api;
          }}
        />
      ) : null}

      {/* Rect-mode marquee preview. */}
      {marqueeRect ? (
        <Box
          data-testid="draw-overlay-preview"
          sx={{
            position: "absolute",
            border: "2px solid #0071E3",
            backgroundColor: "rgba(0,113,227,0.12)",
            pointerEvents: "none",
          }}
          style={marqueeRect}
        />
      ) : null}

      {/* ANA-R4-VALID-01 — crossing-lines refusal notice. */}
      <Snackbar
        open={invalidOpen}
        autoHideDuration={4000}
        onClose={() => {
          setInvalidOpen(false);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="warning"
          variant="filled"
          onClose={() => {
            setInvalidOpen(false);
          }}
          data-testid="draw-crossing-lines-prohibited"
        >
          Crossing lines — prohibited. The edges must not cross.
        </Alert>
      </Snackbar>
    </Box>
  );
}
